// @ts-nocheck
/* Store Custom Code - Start */
import { lazyLoadStyles } from "@store/Themes/common/mixins/lazyload";
import { useEffect, useMemo, useState } from "react";
import { css, FlattenInterpolation, ThemeProps } from "styled-components";

type CssResult = FlattenInterpolation<ThemeProps<any>>;

interface UseBackgroundSkeletonResult {
    /** True once the image has loaded (or if no URL was provided). */
    loaded: boolean;
    /** Merged styles: shimmer while loading, background-image once loaded. */
    css: CssResult;
}

/**
 * Preloads a background image and returns shimmer styles while it loads.
 *
 * @param imageUrl - The background-image URL (falsy = no image, immediately "loaded").
 * @param additionalCss - Optional extra css to merge into the returned styles.
 */
const useBackgroundSkeleton = (
    imageUrl: string | undefined | null,
    additionalCss?: CssResult,
): UseBackgroundSkeletonResult => {
    const [loaded, setLoaded] = useState(!imageUrl);

    useEffect(() => {
        if (!imageUrl) {
            setLoaded(true);
            return;
        }

        setLoaded(false);

        const img = new Image();
        img.onload = () => setLoaded(true);
        img.onerror = () => setLoaded(true);
        img.src = imageUrl;

        return () => {
            img.onload = null;
            img.onerror = null;
        };
    }, [imageUrl]);

    const styles = useMemo(() => {
        if (!loaded) {
            return css`
                ${lazyLoadStyles()}
                ${additionalCss}
            `;
        }

        return css`
            background-image: url(${imageUrl});
            animation: lazyLoadFadeIn 50ms ease;
            ${additionalCss}
        `;
    }, [loaded, imageUrl, additionalCss]);

    return { loaded, css: styles };
};

export default useBackgroundSkeleton;

/**
 * Preloads multiple background images and tracks loaded state per URL.
 * Useful when images are rendered in a loop where hooks can't be called.
 *
 * @param imageUrls - Array of image URLs to preload (falsy entries are ignored).
 * @returns A Set of URLs that have finished loading.
 */
export const useBackgroundSkeletons = (imageUrls: (string | undefined | null)[]): Set<string> => {
    const validUrls = useMemo(() => imageUrls.filter((u): u is string => !!u), [imageUrls.join(",")]);
    const [loadedSet, setLoadedSet] = useState<Set<string>>(() => new Set());

    useEffect(() => {
        if (validUrls.length === 0) {
            return;
        }

        const images: HTMLImageElement[] = [];

        for (const url of validUrls) {
            if (loadedSet.has(url)) {
                continue;
            }
            const img = new Image();
            img.onload = () => setLoadedSet(prev => new Set(prev).add(url));
            img.onerror = () => setLoadedSet(prev => new Set(prev).add(url));
            img.src = url;
            images.push(img);
        }

        return () => {
            for (const img of images) {
                img.onload = null;
                img.onerror = null;
            }
        };
    }, [validUrls.join(",")]);

    return loadedSet;
};
/* Store Custom Code - End */
